import {Button} from '@dropbox/dig-components/buttons';
import {Modal} from '@dropbox/dig-components/modal';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CommentLine} from '@dropbox/dig-icons/assets';
import {LaptopMini} from '@dropbox/dig-illustrations';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {
  // EmployeeRoleLevel,
  Workstream,
  WorkstreamAuditLog as WorkstreamAuditLogType,
  WorkstreamMetric,
  WorkstreamUpdate,
} from 'client';
import {parseAndExtractTextFromLexicalJson} from 'components/DSYS/editor/utils/lexical';
import {EmployeeButton} from 'components/DSYS/EmployeeButton';
import {EmptyState} from 'components/DSYS/EmptyState';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {StatusChip} from 'components/DSYS/StatusChip';
import {UpdatePost} from 'components/DSYS/UpdatePost';
import {TimeAgo} from 'components/shared/TimeAgo';
import {ChangeLogLine, MetricIcon} from 'components/teams/TeamAuditLog';
import {format, parseISO} from 'date-fns';
import {formatMetricForecastPeriod} from 'helpers/metricForecast';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {type ComponentProps, type ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import {WORKSTREAM_METRIC_STATUS_OPTIONS} from 'views/workstreams/detail/WorkstreamMetricStatus';

import {useAllUpdates, useAuditLogs} from './hooks';
import type {ActivityViewType} from './types';
import {asUpdateSummaryLog, UpdateSummaryChanges} from './UpdateSummaryAuditLog';

export const getMetricTitle = (metricJsonValue: string) => {
  try {
    const metric = JSON.parse(metricJsonValue);
    return metric.title ? metric.title : metricJsonValue;
  } catch {
    return metricJsonValue;
  }
};

export const formatMetricType = (type: string) => {
  switch (type) {
    case 'metric':
      return t('metric');
    case 'milestone':
      return t('milestone');
    case 'deliverable':
      return t('deliverable');
    default:
      return '';
  }
};

// Audit payloads can predate the backend's normalization of these metric statuses.
const legacyMetricStatuses = new Set([
  'on_plan',
  'good_above',
  'good_below',
  'bad_above',
  'bad_below',
]);

const formatMetricStatus = (status: string | null | undefined) => {
  const option = WORKSTREAM_METRIC_STATUS_OPTIONS.find(({value}) => value === status);
  if (option) {
    return t(option.labelKey);
  }
  if (status && legacyMetricStatuses.has(status)) {
    return t(status);
  }
  return status?.replace(/_/g, ' ').replace(/^./, (character) => character.toUpperCase()) ?? '';
};

type MetricForecastAuditPayload = {
  actual?: string | null;
  budget?: string | null;
  forecast_date?: string | null;
  id?: number | string | null;
  owner_id?: string | null;
  period?: string | null;
  target?: string | null;
  title?: string | null;
  type?: string | null;
  workstream_metric_id?: number | string | null;
};

type MetricAuditPayload = {
  baseline?: string | null;
  complete?: boolean | null;
  complete_date?: string | null;
  current?: string | null;
  definition?: string | null;
  id?: number | string | null;
  owner_id?: string | null;
  status?: string | null;
  target?: string | null;
  title?: string | null;
  type?: string | null;
};

type MnpiMetricAudienceAuditPayload = {
  metric_id?: number | string | null;
  user_ids?: Array<number | string | null | undefined> | null;
  is_mnpi?: boolean | null;
  mnpi_enabled?: boolean | null;
};

type MetricForecastAuditField =
  | 'actual'
  | 'budget'
  | 'forecast_date'
  | 'owner_id'
  | 'period'
  | 'target';

const hasMetricForecastDisplayValue = (value: unknown) =>
  value !== null && value !== undefined && value !== '';

const metricForecastHasField = (
  payload: MetricForecastAuditPayload | null,
  field: MetricForecastAuditField
) => !!payload && Object.prototype.hasOwnProperty.call(payload, field);

const parseMetricForecastAuditPayload = (
  value: string | null | undefined
): MetricForecastAuditPayload | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    return parsed as MetricForecastAuditPayload;
  } catch {
    return null;
  }
};

const parseMetricAuditPayload = (value: string | null | undefined): MetricAuditPayload | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    return parsed as MetricAuditPayload;
  } catch {
    return null;
  }
};

const parseMnpiMetricAudienceAuditPayload = (
  value: string | null | undefined
): MnpiMetricAudienceAuditPayload | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    return parsed as MnpiMetricAudienceAuditPayload;
  } catch {
    return null;
  }
};

const getArchivedStatusAuditLabel = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }

  try {
    const {archived} = JSON.parse(value);
    if (archived === true) {
      return t('workstream_audit_archived_status_archived');
    }

    if (archived === false) {
      return t('workstream_audit_archived_status_unarchived');
    }
  } catch {
    return null;
  }
  return null;
};

const hasVisibleMetricChange = (log: WorkstreamAuditLogType, includeDefinition = false) => {
  const beforeMetric = parseMetricAuditPayload(log.before);
  const afterMetric = parseMetricAuditPayload(log.after);

  if (!beforeMetric && !afterMetric) {
    return false;
  }

  const {
    title: beforeTitle,
    type: beforeType,
    baseline: beforeBaseline,
    current: beforeCurrent,
    definition: beforeDefinition,
    target: beforeTarget,
    status: beforeStatus,
    owner_id: beforeOwnerId,
    complete: beforeComplete,
  } = beforeMetric ?? {};
  const {
    title: afterTitle,
    type: afterType,
    baseline: afterBaseline,
    current: afterCurrent,
    definition: afterDefinition,
    target: afterTarget,
    status: afterStatus,
    owner_id: afterOwnerId,
    complete: afterComplete,
  } = afterMetric ?? {};

  const nameVisible = beforeTitle !== afterTitle && !!(beforeTitle || afterTitle);
  const typeVisible = beforeType !== afterType && !!(beforeType || afterType);
  const baselineVisible =
    (beforeType !== afterType || beforeBaseline !== afterBaseline) &&
    !!(
      (beforeType !== afterType ? beforeType === 'metric' : beforeBaseline) ||
      (beforeType !== afterType ? afterType === 'metric' : afterBaseline)
    );
  const currentVisible =
    (beforeType !== afterType || beforeCurrent !== afterCurrent) &&
    !!(
      (beforeType !== afterType ? beforeType === 'metric' : beforeCurrent) ||
      (beforeType !== afterType ? afterType === 'metric' : afterCurrent)
    );
  const targetVisible =
    (beforeType !== afterType || beforeTarget !== afterTarget) &&
    !!(
      (beforeType !== afterType ? beforeType === 'metric' : beforeTarget) ||
      (beforeType !== afterType ? afterType === 'metric' : afterTarget)
    );
  const statusVisible =
    (beforeType === 'metric' || afterType === 'metric') &&
    beforeStatus !== afterStatus &&
    !!(beforeStatus || afterStatus);
  const ownerVisible =
    beforeOwnerId !== afterOwnerId &&
    !!((log.before && beforeOwnerId) || (log.after && afterOwnerId));
  const completionVisible = beforeComplete !== afterComplete;
  const definitionVisible = includeDefinition && beforeDefinition !== afterDefinition;

  return (
    nameVisible ||
    typeVisible ||
    baselineVisible ||
    currentVisible ||
    targetVisible ||
    statusVisible ||
    ownerVisible ||
    completionVisible ||
    definitionVisible
  );
};

const hasVisibleMetricForecastChange = (log: WorkstreamAuditLogType) => {
  const beforeForecast = parseMetricForecastAuditPayload(log.before);
  const afterForecast = parseMetricForecastAuditPayload(log.after);

  if (!beforeForecast && !afterForecast) {
    return !!(log.before || log.after);
  }

  const isAddition = !beforeForecast && !!afterForecast;
  const isDeletion = !!beforeForecast && !afterForecast;

  const beforePeriod = beforeForecast?.period ?? null;
  const afterPeriod = afterForecast?.period ?? null;
  const beforeTarget = beforeForecast?.target ?? null;
  const afterTarget = afterForecast?.target ?? null;
  const beforeBudget = beforeForecast?.budget ?? null;
  const afterBudget = afterForecast?.budget ?? null;
  const beforeActual = beforeForecast?.actual ?? null;
  const afterActual = afterForecast?.actual ?? null;
  const beforeForecastDate = beforeForecast?.forecast_date ?? null;
  const afterForecastDate = afterForecast?.forecast_date ?? null;

  if (isAddition) {
    return !!(
      hasMetricForecastDisplayValue(afterTarget) ||
      hasMetricForecastDisplayValue(afterBudget) ||
      hasMetricForecastDisplayValue(afterActual) ||
      hasMetricForecastDisplayValue(afterForecastDate)
    );
  }

  if (isDeletion) {
    return !!(
      hasMetricForecastDisplayValue(beforeTarget) ||
      hasMetricForecastDisplayValue(beforeBudget) ||
      hasMetricForecastDisplayValue(beforeActual) ||
      hasMetricForecastDisplayValue(beforeForecastDate)
    );
  }

  const periodChanged =
    !!beforeForecast &&
    !!afterForecast &&
    metricForecastHasField(beforeForecast, 'period') &&
    metricForecastHasField(afterForecast, 'period') &&
    beforePeriod !== afterPeriod;
  const targetChanged =
    !!beforeForecast &&
    !!afterForecast &&
    metricForecastHasField(beforeForecast, 'target') &&
    metricForecastHasField(afterForecast, 'target') &&
    beforeTarget !== afterTarget;
  const budgetChanged =
    !!beforeForecast &&
    !!afterForecast &&
    metricForecastHasField(beforeForecast, 'budget') &&
    metricForecastHasField(afterForecast, 'budget') &&
    beforeBudget !== afterBudget;
  const actualChanged =
    !!beforeForecast &&
    !!afterForecast &&
    metricForecastHasField(beforeForecast, 'actual') &&
    metricForecastHasField(afterForecast, 'actual') &&
    beforeActual !== afterActual;
  const forecastDateChanged =
    !!beforeForecast &&
    !!afterForecast &&
    metricForecastHasField(beforeForecast, 'forecast_date') &&
    metricForecastHasField(afterForecast, 'forecast_date') &&
    beforeForecastDate !== afterForecastDate;

  return periodChanged || targetChanged || budgetChanged || actualChanged || forecastDateChanged;
};

const shouldRenderAuditLog = (log: WorkstreamAuditLogType, includeDefinition = false) => {
  if (log.type === 'metric') {
    return hasVisibleMetricChange(log, includeDefinition);
  }

  if (log.type === 'metric_forecast') {
    return hasVisibleMetricForecastChange(log);
  }

  // Do not show status update logs for 'archived' since we already log archived_status separately
  if (log.type === 'update' || log.type === 'update_deleted') {
    try {
      const update = JSON.parse(log.after ?? log.before ?? '');
      if (update?.status?.toLowerCase() === 'archived') {
        return false;
      }
    } catch {
      return true;
    }
  }

  return true;
};

const formatMetricForecastDate = (forecastDate: string | null | undefined) => {
  if (!forecastDate) {
    return null;
  }

  const parsedDate = new Date(forecastDate);
  if (Number.isNaN(parsedDate.getTime())) {
    return forecastDate;
  }

  return format(parsedDate, 'MMM dd, yyyy');
};

const getMetricForecastTitle = (title: string | null | undefined) => {
  if (!title) {
    return null;
  }

  return parseAndExtractTextFromLexicalJson(title) || title;
};

const getMetricLookupKey = (metricId: number | string | null | undefined) => {
  if (metricId == null || metricId === '') {
    return null;
  }

  return String(metricId);
};

const isDeletion = (log: WorkstreamAuditLogType) => !!log.before && !log.after;
const isAddition = (log: WorkstreamAuditLogType) => !!log.after && !log.before;

const strategySummaryAuditTypes = new Set(['obstacles_and_opportunities', 'guiding_principles']);
const excludedAuditTypesFromGrouping = new Set(['archived_status', 'update', 'update_deleted']);

const getAuditLogActorKey = (log: WorkstreamAuditLogType) =>
  log.employee.user_id ?? log.employee.ldap ?? log.employee.name ?? 'unknown-actor';

const getAuditLogGroupType = (log: WorkstreamAuditLogType) => {
  if (log.type === 'metric') {
    if (!log.before) {
      return 'metric_added';
    }
    if (!log.after) {
      return 'metric_deleted';
    }
  } else if (log.type === 'custom_privacy') {
    return 'privacy';
  }

  return log.type;
};

const parseStrategySummaryAuditLines = (raw: string | undefined): string[] | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || !parsed.every((line) => typeof line === 'string')) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const multisetStringListDiff = (
  beforeLines: string[],
  afterLines: string[]
): {removed: string[]; added: string[]} => {
  const countMap = (lines: string[]) => {
    const m = new Map<string, number>();
    for (const line of lines) {
      m.set(line, (m.get(line) ?? 0) + 1);
    }
    return m;
  };
  const beforeCounts = countMap(beforeLines);
  const afterCounts = countMap(afterLines);
  const keys = new Set([...beforeCounts.keys(), ...afterCounts.keys()]);
  const removed: string[] = [];
  const added: string[] = [];
  for (const key of keys) {
    const net = (beforeCounts.get(key) ?? 0) - (afterCounts.get(key) ?? 0);
    for (let i = 0; i < net; i += 1) {
      removed.push(key);
    }
    for (let i = 0; i < -net; i += 1) {
      added.push(key);
    }
  }
  return {removed, added};
};

/** Legacy rows stored empty lists as `"[]"`; treat like null so UI shows add-only / remove-only. */
const strategySummaryAuditDisplayValue = (
  logType: string,
  value: string | null | undefined
): string | undefined => {
  if (value == null || value === '') {
    return undefined;
  }
  if (strategySummaryAuditTypes.has(logType) && value === '[]') {
    return undefined;
  }
  return value;
};

const mergeEmployeeLogs = (employeeLogs: WorkstreamAuditLogType[]): WorkstreamAuditLogType[] => {
  const merged: WorkstreamAuditLogType[] = [];

  for (let i = 0; i < employeeLogs.length; i += 2) {
    const a = employeeLogs[i];
    const b = employeeLogs[i + 1];

    // Odd number of logs so keep last entry
    if (!b) {
      merged.push(a);
      break;
    }

    const additionLog = isAddition(a) ? a : isAddition(b) ? b : null;
    const deletionLog = isDeletion(a) ? a : isDeletion(b) ? b : null;

    if (additionLog && deletionLog) {
      merged.push({
        ...additionLog,
        before: deletionLog.before,
        after: additionLog.after,
      });
    } else {
      merged.push(a, b);
    }
  }

  return merged;
};

const sortByDeletionAndAddition = (logs: WorkstreamAuditLogType[]): WorkstreamAuditLogType[] => {
  return logs.sort((a, b) => {
    // Log is not for deletion or addition, so just order first
    if (!isDeletion(a) && !isAddition(a)) {
      return -1;
    }

    // Always order "-" before "+"
    if (isDeletion(a) && isAddition(b)) {
      return -1;
    }
    if (isAddition(a) && isDeletion(b)) {
      return 1;
    }
    // If both are additions or deletions, order by timestamp
    return Date.parse(b.date) - Date.parse(a.date);
  });
};

const getMnpiMetricAudienceMetricKey = (
  beforePayload: MnpiMetricAudienceAuditPayload | null,
  afterPayload: MnpiMetricAudienceAuditPayload | null
) => {
  const metricId = afterPayload?.metric_id ?? beforePayload?.metric_id;
  return metricId == null || metricId === '' ? null : String(metricId);
};

const getMnpiMetricAudienceUserIds = (payload: MnpiMetricAudienceAuditPayload | null) =>
  payload?.user_ids?.map(String).filter(Boolean) ?? [];

const aggregateMnpiMetricAudienceLogs = (
  audienceLogs: WorkstreamAuditLogType[]
): WorkstreamAuditLogType[] => {
  const passthroughLogs: WorkstreamAuditLogType[] = [];
  const logsByMetric = new Map<string, WorkstreamAuditLogType[]>();

  audienceLogs.forEach((log) => {
    const beforePayload = parseMnpiMetricAudienceAuditPayload(log.before);
    const afterPayload = parseMnpiMetricAudienceAuditPayload(log.after);
    const metricKey = getMnpiMetricAudienceMetricKey(beforePayload, afterPayload);

    if (!metricKey) {
      passthroughLogs.push(log);
      return;
    }

    logsByMetric.set(metricKey, [...(logsByMetric.get(metricKey) ?? []), log]);
  });

  const aggregatedLogs: WorkstreamAuditLogType[] = [];

  logsByMetric.forEach((metricLogs) => {
    const chronologicalLogs = [...metricLogs].sort(
      (a, b) => Date.parse(a.date) - Date.parse(b.date)
    );
    const oldestLog = chronologicalLogs[0];
    const newestLog = chronologicalLogs[chronologicalLogs.length - 1];
    const oldestBeforePayload = parseMnpiMetricAudienceAuditPayload(oldestLog.before);
    const newestAfterPayload = parseMnpiMetricAudienceAuditPayload(newestLog.after);
    const beforeUserIds = getMnpiMetricAudienceUserIds(oldestBeforePayload);
    const afterUserIds = getMnpiMetricAudienceUserIds(newestAfterPayload);
    const beforeUserIdSet = new Set(beforeUserIds);
    const afterUserIdSet = new Set(afterUserIds);
    const removedUserIds = beforeUserIds.filter((userId) => !afterUserIdSet.has(userId));
    const addedUserIds = afterUserIds.filter((userId) => !beforeUserIdSet.has(userId));

    if (!removedUserIds.length && !addedUserIds.length) {
      return;
    }

    const newestBeforePayload = parseMnpiMetricAudienceAuditPayload(newestLog.before);
    const basePayload = newestAfterPayload ?? newestBeforePayload ?? {};

    aggregatedLogs.push({
      ...newestLog,
      before: removedUserIds.length
        ? JSON.stringify({...basePayload, user_ids: removedUserIds})
        : null,
      after: addedUserIds.length ? JSON.stringify({...basePayload, user_ids: addedUserIds}) : null,
    });
  });

  return [...passthroughLogs, ...aggregatedLogs].sort(
    (a, b) => Date.parse(b.date) - Date.parse(a.date)
  );
};

export const groupByTimeAndType = (logs: WorkstreamAuditLogType[]) => {
  const grouped: {[key: string]: {[type: string]: WorkstreamAuditLogType[]}} = {};

  logs.forEach((log, index) => {
    const date = new Date(log.date);
    date.setHours(0, 0, 0, 0); // Group logs by local calendar day
    const intervalKey = format(date, 'yyyy-MM-dd');

    const logType = getAuditLogGroupType(log);
    const actorKey = getAuditLogActorKey(log);
    const groupKey = excludedAuditTypesFromGrouping.has(log.type)
      ? `${intervalKey}__${actorKey}__${logType}__${index}`
      : `${intervalKey}__${actorKey}`;

    if (!grouped[groupKey]) {
      grouped[groupKey] = {};
    }

    if (!grouped[groupKey][logType]) {
      grouped[groupKey][logType] = [];
    }

    grouped[groupKey][logType].push(log);
  });

  Object.entries(grouped).forEach(([groupKey, byType]) => {
    Object.entries(byType).forEach(([logType, logsForType]) => {
      // Order by timestamp
      logsForType.sort((a, b) => Date.parse(b.date) - Date.parse(a.date));

      // Group employee logs since we have separate logs for additions and deletions
      if (logType === 'employee') {
        const merged = mergeEmployeeLogs(logsForType);
        logsForType.splice(0, logsForType.length, ...merged);
      }

      if (['privacy', 'dependency'].includes(logType)) {
        const sorted = sortByDeletionAndAddition(logsForType);
        logsForType.splice(0, logsForType.length, ...sorted);
      }

      if (logType === 'mnpi_metric_audience') {
        const aggregated = aggregateMnpiMetricAudienceLogs(logsForType);
        if (aggregated.length) {
          logsForType.splice(0, logsForType.length, ...aggregated);
        } else {
          delete grouped[groupKey][logType];
        }
      }
    });

    if (!Object.keys(byType).length) {
      delete grouped[groupKey];
    }
  });

  return grouped;
};

export const AuditLogReason = ({
  border = 'left',
  reason,
  truncate,
}: {
  reason: string;
  border?: 'top' | 'left';
  truncate?: boolean;
}) => {
  const [expanded, setExpanded] = useState(!truncate);
  return (
    <Box
      borderWidth="1"
      borderLeft={border === 'left' ? 'Solid' : undefined}
      borderTop={border === 'top' ? 'Solid' : undefined}
      borderColor="Border Subtle"
      padding="4"
      cursor={truncate ? 'pointer' : undefined}
      display="flex"
      flexDirection="row"
      // overflow="hidden"
      // whiteSpace={width ? 'nowrap' : undefined}
      alignItems="flex-start"
      {...(truncate ? withShade({style: {gap: 4}}) : {style: {gap: 4}})}
      onClick={truncate ? () => setExpanded(!expanded) : undefined}
    >
      <UIIcon
        src={CommentLine}
        size="small"
        className={atoms({color: 'Text Subtle', flexShrink: 0, marginTop: '2'})}
      />
      <Text
        color="faint"
        size={truncate ? 'small' : undefined}
        className={atoms({flexGrow: 1})}
        style={
          expanded
            ? undefined
            : {
                textOverflow: 'ellipsis',
                whiteSpace: expanded ? undefined : 'nowrap',
                width: 1,
                overflow: 'hidden',
              }
        }
      >
        {reason}
      </Text>
    </Box>
  );
};

const UPDATE_COMMENT_TRUNCATE_LINES = 4;
const ACTIVITY_UPDATE_TEXT_MAX_WIDTH = 'var(--breakpoint__medium)';

const hasOverflowingDescendant = (element: Element): boolean => {
  if (element.scrollHeight > element.clientHeight) {
    return true;
  }

  for (let index = 0; index < element.children.length; index += 1) {
    if (hasOverflowingDescendant(element.children[index])) {
      return true;
    }
  }

  return false;
};

type ExpandableUpdateCommentProps = {
  value: string;
} & Pick<ComponentProps<typeof RichTextArea>, 'profile' | 'textBold' | 'textColor' | 'textSize'>;

const ExpandableUpdateComment = ({
  value,
  profile,
  textBold,
  textColor,
  textSize,
}: ExpandableUpdateCommentProps) => {
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setExpanded(false);
  }, [value]);

  useEffect(() => {
    if (!value || expanded) {
      return;
    }

    const checkTruncated = () => {
      const root = contentRef.current;
      if (!root) {
        return;
      }

      setIsTruncated(hasOverflowingDescendant(root));
    };

    const observer = new ResizeObserver(checkTruncated);
    const frameId = requestAnimationFrame(() => {
      checkTruncated();
      const root = contentRef.current;
      if (root) {
        observer.observe(root);
      }
    });

    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [expanded, value]);

  return (
    <Stack gap="4">
      <div ref={contentRef}>
        <RichTextArea
          key={value}
          value={value}
          profile={profile}
          textBold={textBold}
          textColor={textColor}
          textSize={textSize}
          truncateLines={expanded ? undefined : UPDATE_COMMENT_TRUNCATE_LINES}
        />
      </div>
      {(isTruncated || expanded) && (
        <Box>
          <Button
            variant="transparent"
            size="small"
            aria-expanded={expanded}
            onClick={() => setExpanded((current) => !current)}
          >
            {t(expanded ? 'view_less' : 'view_more')}
          </Button>
        </Box>
      )}
    </Stack>
  );
};

const workstreamUpdateToAuditLogType = (updates: WorkstreamUpdate[]): WorkstreamAuditLogType[] =>
  (updates ?? []).map((update, index) => ({
    date: update.created_at,
    type: 'update',
    before: JSON.stringify((updates ?? [])[index - 1]),
    after: JSON.stringify(update),
    employee: update.author,
    reason: null,
  }));

export const WorkstreamAuditLog = ({
  workstream,
  withUpdates,
  oldestFirst,
  onUpdatesLoaded,
  activityViewType,
}: {
  workstream: Workstream;
  withUpdates?: boolean;
  oldestFirst?: boolean;
  onUpdatesLoaded?: () => void;
  activityViewType?: ActivityViewType;
}) => {
  const {isGtmPilotChangesEnabled} = useAtomValue(growthbookCacheAtom);
  const {data: rawAuditLogs, isLoading} = useAuditLogs(workstream.id);
  const data = useMemo(() => rawAuditLogs?.map(asUpdateSummaryLog), [rawAuditLogs]);
  const {data: allUpdates, isLoading: isLoadingAllUpdates} = useAllUpdates(workstream.id);
  const [showStatusUpdateLog, setShowStatusUpdateLog] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (allUpdates && onUpdatesLoaded) {
      setTimeout(() => onUpdatesLoaded(), 400);
    }
  }, [allUpdates, onUpdatesLoaded]);

  const deletedUpdateLogs: WorkstreamAuditLogType[] = [];
  const statusUpdateLogsById = data
    ?.filter((log) => log.type === 'update')
    .reduce<{[key: number]: WorkstreamAuditLogType[]}>((acc, log) => {
      const after = JSON.parse(log.after ?? '{}');
      if (!after.id) {
        const before = JSON.parse(log.before ?? '{}');
        deletedUpdateLogs.push({
          date: log.date,
          type: 'update_deleted',
          before: log.before,
          after: null,
          employee: log.employee,
          reason: log.reason,
        });
        return {
          ...acc,
          [before.id]: [...(acc[before.id] || []), log],
        };
      }

      return {
        ...acc,
        [after.id]: [...(acc[after.id] || []), log],
      };
    }, {});

  // Detect deleted updates by finding IDs in audit logs that aren't in current workstream updates
  // const currentUpdateIds = new Set((workstream.updates || []).map((update) => update.id));

  // // Find updates that were deleted
  // if (statusUpdateLogsById) {
  //   Object.entries(statusUpdateLogsById).forEach(([updateId, logs]) => {
  //     const numericId = parseInt(updateId, 10);
  //     if (!currentUpdateIds.has(numericId) && logs.length > 0) {
  //       // This update exists in logs but not in current workstream - it was deleted
  //       // Use the last log entry before deletion to create a "deleted" entry
  //       const lastLog = logs[logs.length - 1];
  //       if (lastLog.after) {
  //         deletedUpdateLogs.push({
  //           date: lastLog.date,
  //           type: 'update_deleted',
  //           before: lastLog.after,
  //           after: null, // null after indicates deletion
  //           employee: lastLog.employee,
  //           reason: null,
  //         });
  //       }
  //     }
  //   });
  // }

  const logsAndUpdates = [
    ...(data ?? []).filter((log) => !['update', 'action_type', 'archived'].includes(log.type)),
    ...(withUpdates ? workstreamUpdateToAuditLogType(allUpdates ?? []) : []),
    ...deletedUpdateLogs,
  ]
    .filter((log) => {
      if (activityViewType === 'updates') {
        // If activityViewType is 'updates', only show update-related logs
        return ['update', 'update_deleted', 'update_summary'].includes(log.type);
      } else if (activityViewType === 'outcomes') {
        // If activityViewType is 'outcomes', only show metric-related logs
        return [
          'metric',
          'metric_added',
          'metric_deleted',
          'metric_forecast',
          'mnpi_metric_audience',
          'mnpi_metric_toggle',
        ].includes(log.type);
      }
      return true;
    })
    .filter((log) => shouldRenderAuditLog(log, isGtmPilotChangesEnabled));

  logsAndUpdates.sort((a, b) =>
    oldestFirst
      ? parseISO(`${a.date}Z`).getTime() - parseISO(`${b.date}Z`).getTime()
      : parseISO(`${b.date}Z`).getTime() - parseISO(`${a.date}Z`).getTime()
  );

  const creationLogs = logsAndUpdates
    .filter((log) => log.type === 'workstream')
    .sort((a, b) => parseISO(`${a.date}Z`).getTime() - parseISO(`${b.date}Z`).getTime());
  const nonCreationLogs = logsAndUpdates.filter((log) => log.type !== 'workstream');

  const groupedLogs = groupByTimeAndType(nonCreationLogs);
  const groupedLogEntries = Object.entries(groupedLogs);

  if (isLoading || isLoadingAllUpdates) {
    return (
      <Box width="100%" display="flex" justifyContent="center" marginTop="68">
        <Spinner monochromatic />
      </Box>
    );
  }

  return (
    <Stack>
      {!creationLogs.length && !groupedLogEntries.length ? (
        <WorkstreamAuditLogEmptyState activityViewType={activityViewType} />
      ) : (
        <>
          {groupedLogEntries.map(([timestamp, changesByType], index, entries) => {
            const changeTypes = Object.keys(changesByType);
            const firstChange = changesByType[changeTypes[0]][0];
            const cardTimestamp =
              Object.values(changesByType)
                .flat()
                .reduce<string | null>((latest, change) => {
                  if (!latest || Date.parse(change.date) > Date.parse(latest)) {
                    return change.date;
                  }
                  return latest;
                }, null) ?? firstChange.date;
            const firstMetricRelatedType = changeTypes.find(
              (type) => type === 'metric' || type === 'metric_forecast'
            );

            const isLast = index === entries.length - 1;

            const reasonChanges: typeof changesByType = {};
            if (changesByType['metric_added'] || changesByType['metric_deleted']) {
              reasonChanges['metric_added_deleted'] = [
                ...(reasonChanges['metric_added_deleted'] || []),
                ...(changesByType['metric_added'] || []),
                ...(changesByType['metric_deleted'] || []),
              ].sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
            }
            if (changesByType['end_date']) {
              reasonChanges['end_date'] = changesByType['end_date'];
            }

            // Break early for update_deleted and update

            if (changesByType['update_deleted'] || changesByType['update']) {
              // Handle update_deleted
              if (changesByType['update_deleted']) {
                return changesByType['update_deleted'].map(
                  ({before, date}: WorkstreamAuditLogType, itemIndex, deleteItems) => {
                    if (!before) {
                      return null;
                    }

                    const update = JSON.parse(before);

                    const updateLogs = statusUpdateLogsById?.[update.id] || [];
                    const latestUpdateLog =
                      updateLogs.length > 0 ? updateLogs[updateLogs.length - 1] : null;

                    // Calculate if this item is truly the last one
                    const isItemLast = isLast && itemIndex === deleteItems.length - 1;

                    return (
                      <Box
                        key={`update_deleted-${update.id}-${date}`}
                        position="relative"
                        marginBottom="24"
                      >
                        {!isItemLast && (
                          <Box
                            position="absolute"
                            backgroundColor="Border Subtle"
                            style={{left: 28, bottom: -24, width: 1, height: 24}}
                          />
                        )}
                        <UpdatePost
                          employee={firstChange.employee}
                          update={{created_at: date}}
                          editedOnClick={
                            latestUpdateLog?.date
                              ? () => setShowStatusUpdateLog(update.id)
                              : undefined
                          }
                          featured
                          includeArchived
                        >
                          <Stack gap="10">
                            <Box marginBottom="8">
                              <Text isBold>{t('status_update')}</Text>
                            </Box>
                            <ChangeLogLine
                              change="remove"
                              type="update_deleted"
                              value={update.comment}
                              node={
                                update.comment && (
                                  <Box style={{maxWidth: ACTIVITY_UPDATE_TEXT_MAX_WIDTH}}>
                                    <ExpandableUpdateComment
                                      value={update.comment}
                                      textColor="subtle"
                                    />
                                  </Box>
                                )
                              }
                            />
                          </Stack>
                        </UpdatePost>
                      </Box>
                    );
                  }
                );
              }

              // Handle update
              if (changesByType['update']) {
                return changesByType['update'].map(
                  ({after, date}: WorkstreamAuditLogType, itemIndex, updateItems) => {
                    if (!after) {
                      return null;
                    }

                    const update = JSON.parse(after);

                    const updateLogs = statusUpdateLogsById?.[update.id] || [];
                    const latestUpdateLog =
                      updateLogs.length > 0 ? updateLogs[updateLogs.length - 1] : null;

                    // Calculate if this item is truly the last one
                    const isItemLast = isLast && itemIndex === updateItems.length - 1;

                    return (
                      <Box
                        key={`update-${update.id}-${timestamp}`}
                        position="relative"
                        marginBottom="24"
                      >
                        {!isItemLast && (
                          <Box
                            position="absolute"
                            backgroundColor="Border Subtle"
                            style={{left: 28, bottom: -24, width: 1, height: 24}}
                          />
                        )}
                        <UpdatePost
                          employee={firstChange.employee}
                          update={{created_at: date}}
                          editedOnClick={
                            latestUpdateLog?.date
                              ? () => setShowStatusUpdateLog(update.id)
                              : undefined
                          }
                          featured
                          includeArchived
                        >
                          <Stack gap="8">
                            <Stack key={`update-${after}—${date}`}>
                              <Box width="fit-content" marginBottom="8">
                                <StatusChip status={update.status} includeArchived />
                              </Box>
                              {update.comment && (
                                <Box style={{maxWidth: ACTIVITY_UPDATE_TEXT_MAX_WIDTH}}>
                                  <ExpandableUpdateComment
                                    value={update.comment}
                                    textColor="inherit"
                                    textSize="small"
                                  />
                                </Box>
                              )}
                            </Stack>
                          </Stack>
                        </UpdatePost>
                      </Box>
                    );
                  }
                );
              }
            }

            return (
              <Box
                key={`${firstChange.employee}-${timestamp}`}
                position="relative"
                marginBottom="24"
              >
                {!isLast && (
                  <Box
                    position="absolute"
                    backgroundColor="Border Subtle"
                    style={{left: 28, bottom: -24, width: 1, height: 24}}
                  />
                )}
                <UpdatePost
                  employee={firstChange.employee}
                  update={{created_at: cardTimestamp}}
                  featured
                  includeArchived
                >
                  <Stack gap="8">
                    {!Object.keys(reasonChanges).length ? null : (
                      <Stack gap="8" style={{marginBottom: '16'}}>
                        {Object.entries(reasonChanges).map(([type, changes]) => (
                          <Stack
                            key={`${type}-${timestamp}`}
                            gap={type === 'metric_added_deleted' ? '2' : '8'}
                          >
                            {type === 'update' ? (
                              changes.slice(-1).map(({after, date}) => {
                                if (!after) {
                                  return null;
                                }

                                const update = JSON.parse(after);
                                return (
                                  <Stack key={`${type}-${after}—${date}`}>
                                    <Box width="fit-content">
                                      <StatusChip status={update.status} includeArchived />
                                    </Box>
                                    {update.comment && (
                                      <Box style={{maxWidth: ACTIVITY_UPDATE_TEXT_MAX_WIDTH}}>
                                        <ExpandableUpdateComment
                                          value={update.comment}
                                          textColor="inherit"
                                          textSize="small"
                                        />
                                      </Box>
                                    )}
                                  </Stack>
                                );
                              })
                            ) : type === 'metric_added_deleted' ? (
                              <>
                                <Box marginBottom="8">
                                  <Text isBold>{t(`workstream_audit_${type}`)}</Text>
                                </Box>
                                {changes.map(({before, after, date, reason}) => (
                                  <Stack key={`${type}-${before}-${after}—${date}`} gap="8">
                                    <Stack gap="2">
                                      {before && (
                                        <ChangeLogLine
                                          change="remove"
                                          type={type}
                                          value={before}
                                          node={
                                            <RichTextArea
                                              value={JSON.parse(before).title}
                                              textColor="subtle"
                                            />
                                          }
                                          useFullWidth
                                        />
                                      )}
                                      {after && (
                                        <ChangeLogLine
                                          change="add"
                                          type={type}
                                          value={after}
                                          useFullWidth
                                        />
                                      )}
                                    </Stack>
                                    {reason && (
                                      <Box marginTop="16" marginBottom="16">
                                        <AuditLogReason reason={reason} border="top" />
                                      </Box>
                                    )}
                                  </Stack>
                                ))}
                              </>
                            ) : (
                              <>
                                <Box marginBottom="8">
                                  <Text isBold>{t(`workstream_audit_${type}`)}</Text>
                                </Box>
                                {changes.map(({before, after, date, reason}) => (
                                  <Stack key={`${type}-${before}-${after}—${date}`} gap="8">
                                    <Stack gap="2">
                                      {before && (
                                        <ChangeLogLine
                                          change="remove"
                                          type={type}
                                          value={before}
                                          useFullWidth
                                        />
                                      )}
                                      {after && (
                                        <ChangeLogLine
                                          change="add"
                                          type={type}
                                          value={after}
                                          useFullWidth
                                        />
                                      )}
                                    </Stack>
                                    {reason && (
                                      <Box marginTop="16" marginBottom="16">
                                        <AuditLogReason reason={reason} border="top" />
                                      </Box>
                                    )}
                                  </Stack>
                                ))}
                              </>
                            )}
                          </Stack>
                        ))}
                      </Stack>
                    )}
                    {Object.entries(changesByType).map(([type, changes]) =>
                      [
                        'metric_added',
                        'metric_deleted',
                        'end_date',
                        'update',
                        'update_deleted',
                      ].includes(type) ? null : type === 'metric' ? (
                        type === firstMetricRelatedType ? (
                          <WorkstreamChangesByMetric
                            key={`${type}-${timestamp}`}
                            metricChanges={changesByType['metric'] || []}
                            metricForecastChanges={changesByType['metric_forecast'] || []}
                            metrics={workstream.metrics}
                            includeDefinition={isGtmPilotChangesEnabled}
                          />
                        ) : null
                      ) : type === 'metric_forecast' ? (
                        type === firstMetricRelatedType ? (
                          <WorkstreamChangesByMetric
                            key={`${type}-${timestamp}`}
                            metricChanges={changesByType['metric'] || []}
                            metricForecastChanges={changesByType['metric_forecast'] || []}
                            metrics={workstream.metrics}
                            includeDefinition={isGtmPilotChangesEnabled}
                          />
                        ) : null
                      ) : type === 'update_summary' ? (
                        <UpdateSummaryChanges
                          key={`${type}-${timestamp}`}
                          changes={changes}
                          updates={allUpdates ?? []}
                        />
                      ) : type === 'archived_status' ? (
                        <Stack key={`${type}-${timestamp}`} gap="2">
                          {changes.map(({before, after, date, reason}) => {
                            const archivedStatusLabel = getArchivedStatusAuditLabel(
                              after ?? before
                            );
                            return (
                              archivedStatusLabel && (
                                <Stack key={`${type}-${after ?? before}—${date}`} gap="2">
                                  <Box marginBottom="6">
                                    <Text isBold>{archivedStatusLabel}</Text>
                                  </Box>
                                  {reason && (
                                    <Box marginTop="16" marginBottom="16">
                                      <AuditLogReason reason={reason} border="top" />
                                    </Box>
                                  )}
                                </Stack>
                              )
                            );
                          })}
                        </Stack>
                      ) : (
                        <Stack
                          key={`${type}-${timestamp}`}
                          gap={['privacy', 'dependency'].includes(type) ? '2' : '8'}
                        >
                          <Box marginBottom="8">
                            <Text isBold>{t(`workstream_audit_${type}`)}</Text>
                          </Box>
                          {changes.map(({before, after, date, reason}) => {
                            const displayBefore = strategySummaryAuditDisplayValue(
                              type,
                              before ?? undefined
                            );
                            const displayAfter = strategySummaryAuditDisplayValue(
                              type,
                              after ?? undefined
                            );

                            const beforeLines = parseStrategySummaryAuditLines(displayBefore);
                            const afterLines = parseStrategySummaryAuditLines(displayAfter);
                            const canLineDiff =
                              strategySummaryAuditTypes.has(type) &&
                              beforeLines &&
                              afterLines &&
                              displayBefore &&
                              displayAfter;

                            let body: ReactNode;
                            if (canLineDiff) {
                              const {removed, added} = multisetStringListDiff(
                                beforeLines,
                                afterLines
                              );
                              body = (
                                <Stack gap="2">
                                  {removed.map((line, lineIndex) => (
                                    <ChangeLogLine
                                      key={`r-${lineIndex}-${line}`}
                                      change="remove"
                                      type={type}
                                      value={JSON.stringify([line])}
                                    />
                                  ))}
                                  {added.map((line, lineIndex) => (
                                    <ChangeLogLine
                                      key={`a-${lineIndex}-${line}`}
                                      change="add"
                                      type={type}
                                      value={JSON.stringify([line])}
                                    />
                                  ))}
                                </Stack>
                              );
                            } else {
                              body = (
                                <Stack gap="2">
                                  {displayBefore && (
                                    <ChangeLogLine
                                      change="remove"
                                      type={type}
                                      value={displayBefore}
                                      metrics={workstream.metrics}
                                    />
                                  )}
                                  {displayAfter && (
                                    <ChangeLogLine
                                      change="add"
                                      type={type}
                                      value={displayAfter}
                                      metrics={workstream.metrics}
                                    />
                                  )}
                                </Stack>
                              );
                            }

                            return (
                              <Stack
                                key={`${type}-${displayBefore}-${displayAfter}—${date}`}
                                gap="8"
                              >
                                {body}
                                {reason && (
                                  <Box marginTop="16" marginBottom="16">
                                    <AuditLogReason reason={reason} border="top" />
                                  </Box>
                                )}
                              </Stack>
                            );
                          })}
                        </Stack>
                      )
                    )}
                  </Stack>
                </UpdatePost>
              </Box>
            );
          })}
          {creationLogs.map((log, index) => {
            const isLast = index === creationLogs.length - 1;

            return (
              <Box key={`workstream-${log.date}-${index}`} position="relative" marginBottom="24">
                {!isLast && (
                  <Box
                    position="absolute"
                    backgroundColor="Border Subtle"
                    style={{left: 28, bottom: -24, width: 1, height: 24}}
                  />
                )}
                <UpdatePost
                  employee={log.employee}
                  update={{created_at: log.date}}
                  featured
                  includeArchived
                >
                  <Stack gap="2">
                    <Box marginBottom="6">
                      <Text isBold>{t('audit_log_new_workstream')}</Text>
                    </Box>
                  </Stack>
                </UpdatePost>
              </Box>
            );
          })}
        </>
      )}
      {showStatusUpdateLog && (
        <StatusUpdateLogModal
          statusUpdateLog={statusUpdateLogsById?.[showStatusUpdateLog] || []}
          onClose={() => setShowStatusUpdateLog(undefined)}
        />
      )}
    </Stack>
  );
};

const WorkstreamMetricChangeDetails = ({
  log,
  includeDefinition = false,
}: {
  log: WorkstreamAuditLogType;
  includeDefinition?: boolean;
}) => {
  const {before, after, reason} = log;
  const beforeMetric = parseMetricAuditPayload(before);
  const afterMetric = parseMetricAuditPayload(after);

  if (!beforeMetric && !afterMetric) {
    return null;
  }

  if (!hasVisibleMetricChange(log, includeDefinition)) {
    return null;
  }

  const {
    title: beforeTitle,
    type: beforeType,
    baseline: beforeBaseline,
    current: beforeCurrent,
    definition: beforeDefinition,
    target: beforeTarget,
    status: beforeStatus,
    owner_id: beforeOwnerId,
    complete: beforeComplete,
  } = beforeMetric ?? {};
  const {
    title: afterTitle,
    type: afterType,
    baseline: afterBaseline,
    current: afterCurrent,
    definition: afterDefinition,
    target: afterTarget,
    status: afterStatus,
    owner_id: afterOwnerId,
    complete: afterComplete,
  } = afterMetric ?? {};

  return (
    <Stack gap="10">
      {beforeTitle !== afterTitle && (
        <Stack gap="2">
          {beforeTitle && (
            <ChangeLogLine
              change="remove"
              type="metric"
              value={t('workstream_audit_log_metric_name', {
                name: parseAndExtractTextFromLexicalJson(beforeTitle),
              })}
            />
          )}
          {afterTitle && (
            <ChangeLogLine
              change="add"
              type="metric"
              value={t('workstream_audit_log_metric_name', {
                name: parseAndExtractTextFromLexicalJson(afterTitle),
              })}
            />
          )}
        </Stack>
      )}
      {beforeType !== afterType && (
        <Stack gap="2">
          {beforeType && (
            <ChangeLogLine
              change="remove"
              type="metric"
              value={t('workstream_audit_log_metric_type', {
                type: formatMetricType(beforeType),
              })}
            />
          )}
          {afterType && (
            <ChangeLogLine
              change="add"
              type="metric"
              value={t('workstream_audit_log_metric_type', {
                type: formatMetricType(afterType),
              })}
            />
          )}
        </Stack>
      )}
      {(beforeType !== afterType || beforeBaseline !== afterBaseline) && (
        <Stack gap="2">
          {(beforeType !== afterType ? beforeType === 'metric' : beforeBaseline) && (
            <ChangeLogLine
              change="remove"
              type="metric"
              value={t('workstream_audit_log_metric_baseline', {
                baseline: beforeBaseline,
              })}
            />
          )}
          {(beforeType !== afterType ? afterType === 'metric' : afterBaseline) && (
            <ChangeLogLine
              change="add"
              type="metric"
              value={t('workstream_audit_log_metric_baseline', {
                baseline: afterBaseline,
              })}
            />
          )}
        </Stack>
      )}
      {(beforeType !== afterType || beforeCurrent !== afterCurrent) && (
        <Stack gap="2">
          {(beforeType !== afterType ? beforeType === 'metric' : beforeCurrent) && (
            <ChangeLogLine
              change="remove"
              type="metric"
              value={t('workstream_audit_log_metric_current', {
                current: beforeCurrent,
              })}
            />
          )}
          {(beforeType !== afterType ? afterType === 'metric' : afterCurrent) && (
            <ChangeLogLine
              change="add"
              type="metric"
              value={t('workstream_audit_log_metric_current', {
                current: afterCurrent,
              })}
            />
          )}
        </Stack>
      )}
      {(beforeType !== afterType || beforeTarget !== afterTarget) && (
        <Stack gap="2">
          {(beforeType !== afterType ? beforeType === 'metric' : beforeTarget) && (
            <ChangeLogLine
              change="remove"
              type="metric"
              value={t('workstream_audit_log_metric_target', {
                target: beforeTarget,
              })}
            />
          )}
          {(beforeType !== afterType ? afterType === 'metric' : afterTarget) && (
            <ChangeLogLine
              change="add"
              type="metric"
              value={t('workstream_audit_log_metric_target', {
                target: afterTarget,
              })}
            />
          )}
        </Stack>
      )}
      {(beforeType === 'metric' || afterType === 'metric') && beforeStatus !== afterStatus && (
        <Stack gap="2">
          {beforeStatus && (
            <ChangeLogLine
              change="remove"
              type="metric"
              value={t('workstream_audit_log_metric_status', {
                status: formatMetricStatus(beforeStatus),
              })}
            />
          )}
          {afterStatus && (
            <ChangeLogLine
              change="add"
              type="metric"
              value={t('workstream_audit_log_metric_status', {
                status: formatMetricStatus(afterStatus),
              })}
            />
          )}
        </Stack>
      )}
      {beforeOwnerId !== afterOwnerId && (
        <Stack gap="2">
          {before && beforeOwnerId && (
            <ChangeLogLine change="remove" type="metric_dri" value={before} />
          )}
          {after && afterOwnerId && <ChangeLogLine change="add" type="metric_dri" value={after} />}
        </Stack>
      )}
      {beforeComplete !== afterComplete && (
        <Stack gap="2">
          <ChangeLogLine
            change="remove"
            type="metric"
            value={
              beforeComplete
                ? t('workstream_audit_log_metric_status_complete')
                : t('workstream_audit_log_metric_status_incomplete')
            }
          />
          <ChangeLogLine
            change="add"
            type="metric"
            value={
              afterComplete
                ? t('workstream_audit_log_metric_status_complete')
                : t('workstream_audit_log_metric_status_incomplete')
            }
          />
        </Stack>
      )}
      {includeDefinition && beforeDefinition !== afterDefinition && (
        <Stack gap="2">
          {beforeDefinition && (
            <ChangeLogLine
              change="remove"
              type="metric"
              value={t('workstream_audit_log_metric_definition', {
                definition: beforeDefinition,
              })}
            />
          )}
          {afterDefinition && (
            <ChangeLogLine
              change="add"
              type="metric"
              value={t('workstream_audit_log_metric_definition', {
                definition: afterDefinition,
              })}
            />
          )}
        </Stack>
      )}
      {reason && <AuditLogReason reason={reason} border="top" />}
    </Stack>
  );
};

const WorkstreamMetricForecastChangeDetails = ({log}: {log: WorkstreamAuditLogType}) => {
  const {before, after, reason, date} = log;
  const beforeForecast = parseMetricForecastAuditPayload(before);
  const afterForecast = parseMetricForecastAuditPayload(after);

  if (!beforeForecast && !afterForecast) {
    return (
      <Stack key={`metric_forecast-fallback-${before}-${after}-${date}`} gap="8">
        <Stack gap="2">
          {before && <ChangeLogLine change="remove" type="metric_forecast" value={before} />}
          {after && <ChangeLogLine change="add" type="metric_forecast" value={after} />}
        </Stack>
        {reason && <AuditLogReason reason={reason} border="top" />}
      </Stack>
    );
  }

  if (!hasVisibleMetricForecastChange(log)) {
    return null;
  }

  const isForecastAddition = !beforeForecast && !!afterForecast;
  const isForecastDeletion = !!beforeForecast && !afterForecast;
  const operationLabel = isForecastAddition
    ? t('workstream_audit_log_metric_forecast_added')
    : isForecastDeletion
      ? t('workstream_audit_log_metric_forecast_removed')
      : t('workstream_audit_log_metric_forecast_updated');

  const beforePeriod = beforeForecast?.period ?? null;
  const afterPeriod = afterForecast?.period ?? null;
  const beforeTarget = beforeForecast?.target ?? null;
  const afterTarget = afterForecast?.target ?? null;
  const beforeBudget = beforeForecast?.budget ?? null;
  const afterBudget = afterForecast?.budget ?? null;
  const beforeActual = beforeForecast?.actual ?? null;
  const afterActual = afterForecast?.actual ?? null;
  const beforeForecastDate = beforeForecast?.forecast_date ?? null;
  const afterForecastDate = afterForecast?.forecast_date ?? null;
  const periodContext = formatMetricForecastPeriod(afterPeriod ?? beforePeriod);

  const periodChanged =
    !!beforeForecast &&
    !!afterForecast &&
    metricForecastHasField(beforeForecast, 'period') &&
    metricForecastHasField(afterForecast, 'period') &&
    beforePeriod !== afterPeriod;
  const targetChanged =
    !!beforeForecast &&
    !!afterForecast &&
    metricForecastHasField(beforeForecast, 'target') &&
    metricForecastHasField(afterForecast, 'target') &&
    beforeTarget !== afterTarget;
  const budgetChanged =
    !!beforeForecast &&
    !!afterForecast &&
    metricForecastHasField(beforeForecast, 'budget') &&
    metricForecastHasField(afterForecast, 'budget') &&
    beforeBudget !== afterBudget;
  const actualChanged =
    !!beforeForecast &&
    !!afterForecast &&
    metricForecastHasField(beforeForecast, 'actual') &&
    metricForecastHasField(afterForecast, 'actual') &&
    beforeActual !== afterActual;
  const forecastDateChanged =
    !!beforeForecast &&
    !!afterForecast &&
    metricForecastHasField(beforeForecast, 'forecast_date') &&
    metricForecastHasField(afterForecast, 'forecast_date') &&
    beforeForecastDate !== afterForecastDate;
  const hasRenderedDiff =
    periodChanged || targetChanged || budgetChanged || actualChanged || forecastDateChanged;

  const periodLabel = (period: string | null | undefined) =>
    hasMetricForecastDisplayValue(period)
      ? `${t('workstream_audit_log_metric_forecast_period')}: ${formatMetricForecastPeriod(period)}`
      : null;
  const targetLabel = (value: string | null | undefined) =>
    hasMetricForecastDisplayValue(value) ? `${t('target')}: ${value}` : null;
  const budgetLabel = (value: string | null | undefined) =>
    hasMetricForecastDisplayValue(value) ? `${t('budget')}: ${value}` : null;
  const actualLabel = (value: string | null | undefined) =>
    hasMetricForecastDisplayValue(value) ? `${t('actual')}: ${value}` : null;
  const forecastDateLabel = (value: string | null | undefined) =>
    hasMetricForecastDisplayValue(value)
      ? `${t('workstream_audit_log_metric_forecast_date')}: ${formatMetricForecastDate(value)}`
      : null;

  return (
    <Stack gap="8">
      <Box display="flex" alignItems="baseline" flexWrap="wrap" style={{gap: 8}}>
        {periodContext && <Text color="subtle">{periodContext}</Text>}
        <Text color="subtle">{operationLabel}</Text>
      </Box>

      <Stack gap="10">
        {isForecastDeletion && (
          <Stack gap="2">
            {targetLabel(beforeTarget) && (
              <ChangeLogLine
                change="remove"
                type="metric_forecast"
                value={targetLabel(beforeTarget) ?? ''}
              />
            )}
            {budgetLabel(beforeBudget) && (
              <ChangeLogLine
                change="remove"
                type="metric_forecast"
                value={budgetLabel(beforeBudget) ?? ''}
              />
            )}
            {actualLabel(beforeActual) && (
              <ChangeLogLine
                change="remove"
                type="metric_forecast"
                value={actualLabel(beforeActual) ?? ''}
              />
            )}
            {forecastDateLabel(beforeForecastDate) && (
              <ChangeLogLine
                change="remove"
                type="metric_forecast"
                value={forecastDateLabel(beforeForecastDate) ?? ''}
              />
            )}
          </Stack>
        )}
        {isForecastAddition && (
          <Stack gap="2">
            {targetLabel(afterTarget) && (
              <ChangeLogLine
                change="add"
                type="metric_forecast"
                value={targetLabel(afterTarget) ?? ''}
              />
            )}
            {budgetLabel(afterBudget) && (
              <ChangeLogLine
                change="add"
                type="metric_forecast"
                value={budgetLabel(afterBudget) ?? ''}
              />
            )}
            {actualLabel(afterActual) && (
              <ChangeLogLine
                change="add"
                type="metric_forecast"
                value={actualLabel(afterActual) ?? ''}
              />
            )}
            {forecastDateLabel(afterForecastDate) && (
              <ChangeLogLine
                change="add"
                type="metric_forecast"
                value={forecastDateLabel(afterForecastDate) ?? ''}
              />
            )}
          </Stack>
        )}
        {!isForecastAddition && !isForecastDeletion && periodChanged && (
          <Stack gap="2">
            {periodLabel(beforePeriod) && (
              <ChangeLogLine
                change="remove"
                type="metric_forecast"
                value={periodLabel(beforePeriod) ?? ''}
              />
            )}
            {periodLabel(afterPeriod) && (
              <ChangeLogLine
                change="add"
                type="metric_forecast"
                value={periodLabel(afterPeriod) ?? ''}
              />
            )}
          </Stack>
        )}
        {!isForecastAddition && !isForecastDeletion && targetChanged && (
          <Stack gap="2">
            {targetLabel(beforeTarget) && (
              <ChangeLogLine
                change="remove"
                type="metric_forecast"
                value={targetLabel(beforeTarget) ?? ''}
              />
            )}
            {targetLabel(afterTarget) && (
              <ChangeLogLine
                change="add"
                type="metric_forecast"
                value={targetLabel(afterTarget) ?? ''}
              />
            )}
          </Stack>
        )}
        {!isForecastAddition && !isForecastDeletion && budgetChanged && (
          <Stack gap="2">
            {budgetLabel(beforeBudget) && (
              <ChangeLogLine
                change="remove"
                type="metric_forecast"
                value={budgetLabel(beforeBudget) ?? ''}
              />
            )}
            {budgetLabel(afterBudget) && (
              <ChangeLogLine
                change="add"
                type="metric_forecast"
                value={budgetLabel(afterBudget) ?? ''}
              />
            )}
          </Stack>
        )}
        {!isForecastAddition && !isForecastDeletion && actualChanged && (
          <Stack gap="2">
            {actualLabel(beforeActual) && (
              <ChangeLogLine
                change="remove"
                type="metric_forecast"
                value={actualLabel(beforeActual) ?? ''}
              />
            )}
            {actualLabel(afterActual) && (
              <ChangeLogLine
                change="add"
                type="metric_forecast"
                value={actualLabel(afterActual) ?? ''}
              />
            )}
          </Stack>
        )}
        {!isForecastAddition && !isForecastDeletion && forecastDateChanged && (
          <Stack gap="2">
            {forecastDateLabel(beforeForecastDate) && (
              <ChangeLogLine
                change="remove"
                type="metric_forecast"
                value={forecastDateLabel(beforeForecastDate) ?? ''}
              />
            )}
            {forecastDateLabel(afterForecastDate) && (
              <ChangeLogLine
                change="add"
                type="metric_forecast"
                value={forecastDateLabel(afterForecastDate) ?? ''}
              />
            )}
          </Stack>
        )}
        {!isForecastAddition && !isForecastDeletion && !hasRenderedDiff && (
          <Stack gap="2">
            {before && <ChangeLogLine change="remove" type="metric_forecast" value={before} />}
            {after && <ChangeLogLine change="add" type="metric_forecast" value={after} />}
          </Stack>
        )}
      </Stack>
      {reason && <AuditLogReason reason={reason} border="top" />}
    </Stack>
  );
};

type MetricRelatedChangeEntry = {
  groupKey: string;
  kind: 'metric' | 'metric_forecast';
  metricType: string;
  sequence: number;
  sortDate: number;
  title: string | null;
  log: WorkstreamAuditLogType;
};

const getMetricChangeGroupKey = (
  metricId: number | string | null | undefined,
  title: string | null,
  fallback: string
) => {
  const metricLookupKey = getMetricLookupKey(metricId);
  if (metricLookupKey) {
    return `metric:${metricLookupKey}`;
  }
  if (title) {
    return `metric-title:${title}`;
  }
  return fallback;
};

export const WorkstreamChangesByMetric = ({
  metricChanges,
  metricForecastChanges = [],
  metrics = [],
  includeDefinition = false,
}: {
  metricChanges: WorkstreamAuditLogType[];
  metricForecastChanges?: WorkstreamAuditLogType[];
  metrics?: WorkstreamMetric[];
  includeDefinition?: boolean;
}) => {
  const metricsById = new Map(metrics.map((metric) => [String(metric.id), metric]));
  const metricsByForecastId = new Map(
    metrics.flatMap((metric) =>
      (metric.metric_forecasts ?? [])
        .filter((forecast) => forecast.id != null)
        .map((forecast) => [String(forecast.id), metric] as const)
    )
  );

  const metricEntries: MetricRelatedChangeEntry[] = metricChanges.map((log, index) => {
    const beforeMetric = parseMetricAuditPayload(log.before);
    const afterMetric = parseMetricAuditPayload(log.after);
    const metricLookupKey =
      getMetricLookupKey(afterMetric?.id) ?? getMetricLookupKey(beforeMetric?.id);
    const currentMetric = metricLookupKey ? metricsById.get(metricLookupKey) : undefined;
    const title =
      getMetricForecastTitle(afterMetric?.title) ??
      getMetricForecastTitle(beforeMetric?.title) ??
      getMetricForecastTitle(currentMetric?.title);
    const metricId = afterMetric?.id ?? beforeMetric?.id ?? currentMetric?.id;
    return {
      groupKey: getMetricChangeGroupKey(metricId, title, `metric-fallback:${log.date}:${index}`),
      kind: 'metric',
      metricType: afterMetric?.type ?? beforeMetric?.type ?? currentMetric?.type ?? 'metric',
      sequence: index,
      sortDate: Date.parse(log.date),
      title,
      log,
    };
  });

  const metricForecastEntries: MetricRelatedChangeEntry[] = metricForecastChanges.map(
    (log, index) => {
      const beforeForecast = parseMetricForecastAuditPayload(log.before);
      const afterForecast = parseMetricForecastAuditPayload(log.after);
      const metricLookupKey =
        getMetricLookupKey(afterForecast?.workstream_metric_id) ??
        getMetricLookupKey(beforeForecast?.workstream_metric_id);
      const forecastLookupKey =
        getMetricLookupKey(afterForecast?.id) ?? getMetricLookupKey(beforeForecast?.id);
      const currentMetric =
        (metricLookupKey ? metricsById.get(metricLookupKey) : undefined) ??
        (forecastLookupKey ? metricsByForecastId.get(forecastLookupKey) : undefined);
      const title =
        getMetricForecastTitle(afterForecast?.title) ??
        getMetricForecastTitle(beforeForecast?.title) ??
        getMetricForecastTitle(currentMetric?.title);
      const metricId =
        afterForecast?.workstream_metric_id ??
        beforeForecast?.workstream_metric_id ??
        currentMetric?.id;
      return {
        groupKey: getMetricChangeGroupKey(
          metricId,
          title,
          `metric-forecast-fallback:${log.date}:${index}`
        ),
        kind: 'metric_forecast',
        metricType: afterForecast?.type ?? beforeForecast?.type ?? currentMetric?.type ?? 'metric',
        sequence: metricChanges.length + index,
        sortDate: Date.parse(log.date),
        title,
        log,
      };
    }
  );

  const metricRelatedEntries = [...metricEntries, ...metricForecastEntries].sort((a, b) => {
    if (a.sortDate !== b.sortDate) {
      return b.sortDate - a.sortDate;
    }
    return b.sequence - a.sequence;
  });

  const groupedEntries = new Map<
    string,
    {entries: MetricRelatedChangeEntry[]; metricType: string; title: string | null}
  >();

  metricRelatedEntries.forEach((entry) => {
    const existing = groupedEntries.get(entry.groupKey);
    if (!existing) {
      groupedEntries.set(entry.groupKey, {
        entries: [entry],
        metricType: entry.metricType,
        title: entry.title,
      });
      return;
    }

    existing.entries.push(entry);
    if (!existing.title && entry.title) {
      existing.title = entry.title;
    }
    if (existing.metricType === 'metric' && entry.metricType !== 'metric') {
      existing.metricType = entry.metricType;
    }
  });

  return (
    <Stack gap="16">
      {[...groupedEntries.entries()].map(([groupKey, group]) => {
        const visibleEntries = group.entries.filter((entry) =>
          entry.kind === 'metric'
            ? hasVisibleMetricChange(entry.log, includeDefinition)
            : hasVisibleMetricForecastChange(entry.log)
        );

        if (!visibleEntries.length) {
          return null;
        }

        return (
          <Stack key={groupKey} gap="10">
            {group.title && (
              <Box display="flex" alignItems="flex-start" marginBottom="8">
                <MetricIcon metricType={group.metricType} />
                <Text isBold>{group.title}</Text>
              </Box>
            )}
            <Stack gap="10">
              {visibleEntries.map((entry) =>
                entry.kind === 'metric' ? (
                  <WorkstreamMetricChangeDetails
                    key={`metric-entry-${groupKey}-${entry.log.date}`}
                    log={entry.log}
                    includeDefinition={includeDefinition}
                  />
                ) : (
                  <WorkstreamMetricForecastChangeDetails
                    key={`metric-forecast-entry-${groupKey}-${entry.log.date}`}
                    log={entry.log}
                  />
                )
              )}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

const WorkstreamAuditLogEmptyState = ({
  activityViewType = 'all',
}: {
  activityViewType?: ActivityViewType;
}) => {
  return (
    <EmptyState
      title={t(`workstream_audit_log_empty_title_${activityViewType}`)}
      body={t(`workstream_audit_log_empty_body_${activityViewType}`)}
      image={<LaptopMini width="64" altText={''} />}
    />
  );
};

// function getLatestMetrics(entries?: WorkstreamAuditLogType[]): WorkstreamAuditLogType[] {
//   const latestMap = new Map<string, WorkstreamAuditLogType>(); // Maps `title` to latest WorkstreamAuditLogType
//   const latestTimestamps = new Map<string, string>(); // Tracks latest timestamp for each `title`
//   const beforeToAfter = new Map<string, string>(); // Maps `before.title` to `after.title` for tracking transitions

//   entries?.forEach((entry) => {
//     if (!entry.after) return; // Ignore entries with no 'after' data

//     const afterData = JSON.parse(entry.after);
//     const afterTitle = afterData.title;
//     const entryDate = new Date(entry.date).toISOString();

//     // If there's a before, track its transition
//     if (entry.before) {
//       const beforeData = JSON.parse(entry.before);
//       const beforeTitle = beforeData.title;
//       beforeToAfter.set(beforeTitle, afterTitle);
//     }

//     // Store the latest WorkstreamAuditLogType object per `after.title`
//     if (!latestTimestamps.has(afterTitle) || entryDate > latestTimestamps.get(afterTitle)!) {
//       latestTimestamps.set(afterTitle, entryDate);
//       latestMap.set(afterTitle, entry);
//     }
//   });

//   // Resolve final latest values by following before → after transitions
//   const finalEntries = new Set<WorkstreamAuditLogType>();

//   latestTimestamps.forEach((_, title) => {
//     const latestTitle = title;
//     // while (beforeToAfter.has(latestTitle)) {
//     //   latestTitle = beforeToAfter.get(latestTitle)!;
//     // }
//     if (latestMap.has(latestTitle)) {
//       finalEntries.add(latestMap.get(latestTitle)!);
//     }
//   });

//   return Array.from(finalEntries);
// }

const StatusUpdateLogModal = ({
  statusUpdateLog,
  onClose,
}: {
  statusUpdateLog: WorkstreamAuditLogType[];
  onClose: () => void;
}) => {
  // const latestUpdate = statusUpdateLog[statusUpdateLog.length - 1];
  // const lastestAfter = latestUpdate?.after ? JSON.parse(latestUpdate.after) : null;
  const sortedStatusUpdateLog = [...statusUpdateLog].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return (
    <Modal onRequestClose={onClose} withCloseButton="Close" open>
      <Modal.Header>
        <Modal.Title>
          <Box display="flex" alignItems="center">
            {/* <Box as={UIIcon} src={PersonMultipleLine} marginRight="8" /> */}
            {t('update_history')}
          </Box>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Stack>
          {sortedStatusUpdateLog.map((log, index) => {
            const after = log.before ? JSON.parse(log.before) : null;
            return (
              <Box
                key={log.date}
                borderBottom={index < sortedStatusUpdateLog.length - 1 ? 'Solid' : undefined}
                borderColor={index < sortedStatusUpdateLog.length - 1 ? 'Border Subtle' : undefined}
                paddingY="16"
                style={{marginTop: index === 0 ? -16 : undefined}}
              >
                <Stack gap="8">
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <EmployeeButton employee={log.employee} />
                    <Text size="small" color="subtle">
                      <TimeAgo timestamp={log.date} />
                    </Text>
                  </Box>

                  <Box width="fit-content">
                    <StatusChip status={after.status} includeArchived />
                  </Box>
                  {/* The update's one-line summary, so a summary-only edit isn't an empty entry. */}
                  {after.summary && (
                    <Text size="small" color="subtle" style={{fontStyle: 'italic'}}>
                      {after.summary}
                    </Text>
                  )}
                  {after.comment && (
                    <RichTextArea textColor="inherit" textSize="small" value={after.comment} />
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Modal.Body>
    </Modal>
  );
};

// const LoadableEmployeeAvatar = ({ownerId}: {ownerId: string}) => {
//   const {employee, isLoading} = useEmployee({ldap: ownerId});

//   if (isLoading) {
//     return <Skeleton.Avatar size="small" />;
//   }

//   if (!employee) {
//     return null;
//   }

//   return <Avatar user={employee} size="small" />;
// };
